import { useState, useCallback, useEffect } from 'react';
import { Upload, Download, Trash2, Image as ImageIcon, Tag, WifiOff, X, ChevronLeft, ChevronRight, PenTool } from '../../icons/wp-icons';
import { uploadFoto, getFotoUrl, updateFoto, deleteFoto } from '../../api/fotoApi';
import toast from 'react-hot-toast';

function FotoUpload({ kundeId, fotos, anfragen = [], onFotosChange, readOnly = false, onSkizzieren = null }) {
  const [dragActive, setDragActive] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [selectedAnfrageId, setSelectedAnfrageId] = useState('');
  const [editingFotoId, setEditingFotoId] = useState(null);
  const [filterAnfrageId, setFilterAnfrageId] = useState('all');
  const [lightboxFoto, setLightboxFoto] = useState(null); // Für vergrößerte Ansicht

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback(async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      await handleFiles(e.dataTransfer.files);
    }
  }, [kundeId, selectedAnfrageId]);

  const handleChange = useCallback(async (e) => {
    e.preventDefault();
    if (e.target.files && e.target.files[0]) {
      await handleFiles(e.target.files);
      e.target.value = '';
    }
  }, [kundeId, selectedAnfrageId]);

  const handleFiles = async (files) => {
    setError('');
    setUploading(true);

    try {
      const file = files[0];

      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'application/pdf'];
      if (!allowedTypes.includes(file.type)) {
        setError('Nur Bilder (JPG, PNG, GIF) und PDFs sind erlaubt');
        setUploading(false);
        return;
      }

      if (file.size > 10485760) {
        setError('Datei ist zu gross (max. 10MB)');
        setUploading(false);
        return;
      }

      const result = await uploadFoto(kundeId, file, '', selectedAnfrageId || null);

      // Offline-Benachrichtigung anzeigen
      if (result.offline) {
        toast((t) => (
          <div className="flex items-center gap-3">
            <WifiOff className="w-5 h-5 text-orange-500 flex-shrink-0" />
            <div>
              <p className="font-medium">Foto offline gespeichert</p>
              <p className="text-sm text-gray-600">Wird hochgeladen sobald Sie wieder online sind</p>
            </div>
          </div>
        ), {
          duration: 4000,
          style: {
            background: '#FFF7ED',
            border: '1px solid #FDBA74',
          }
        });
      }

      onFotosChange();
    } catch (err) {
      console.error('Upload error:', err);
      setError(err.response?.data?.error || 'Fehler beim Hochladen');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (fotoId) => {
    if (!window.confirm('Möchten Sie dieses Foto wirklich löschen?')) {
      return;
    }

    try {
      await deleteFoto(fotoId);
      onFotosChange();
    } catch (err) {
      console.error('Delete error:', err);
      setError(err.response?.data?.error || 'Fehler beim Loeschen');
    }
  };

  const handleDownload = (foto) => {
    const url = getFotoUrl(foto);
    const link = document.createElement('a');
    link.href = url;
    link.download = foto.dateiname;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleAnfrageChange = async (fotoId, anfrageId) => {
    try {
      await updateFoto(fotoId, { anfrageId: anfrageId || '' });
      setEditingFotoId(null);
      onFotosChange();
    } catch (err) {
      console.error('Update error:', err);
      setError(err.response?.data?.error || 'Fehler beim Aktualisieren');
    }
  };

  // Filter fotos by selected anfrage
  const filteredFotos = fotos?.filter(foto => {
    if (filterAnfrageId === 'all') return true;
    if (filterAnfrageId === 'untagged') return !foto.anfrageId;
    return foto.anfrageId === filterAnfrageId;
  }) || [];

  // Lightbox Navigation
  const currentLightboxIndex = lightboxFoto ? filteredFotos.findIndex(f => f.id === lightboxFoto.id) : -1;

  const navigateLightbox = (direction) => {
    if (filteredFotos.length === 0) return;
    let newIndex = currentLightboxIndex + direction;
    if (newIndex < 0) newIndex = filteredFotos.length - 1;
    if (newIndex >= filteredFotos.length) newIndex = 0;
    setLightboxFoto(filteredFotos[newIndex]);
  };

  // Keyboard navigation für Lightbox
  useEffect(() => {
    if (!lightboxFoto) return;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setLightboxFoto(null);
      } else if (e.key === 'ArrowLeft') {
        navigateLightbox(-1);
      } else if (e.key === 'ArrowRight') {
        navigateLightbox(1);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    // Prevent body scroll when lightbox is open
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [lightboxFoto, currentLightboxIndex, filteredFotos]);

  // Foto in Lightbox öffnen (nur für Bilder, nicht PDFs)
  const openLightbox = (foto) => {
    if (!foto.dateiname.toLowerCase().endsWith('.pdf')) {
      setLightboxFoto(foto);
    }
  };

  // Touch/Swipe-Handling für Mobile
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const minSwipeDistance = 50;

  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe && filteredFotos.length > 1) {
      navigateLightbox(1); // Next
    } else if (isRightSwipe && filteredFotos.length > 1) {
      navigateLightbox(-1); // Previous
    }
  };

  return (
    <div className="space-y-4">
      {/* Upload Zone with Anfrage Selection - nur wenn nicht readOnly */}
      {!readOnly && (
        <div className="space-y-3">
          {anfragen && anfragen.length > 0 && (
            <div className="flex items-center gap-2 sm:gap-3">
              <Tag className="w-4 h-4 text-text-muted shrink-0" />
              <select
                value={selectedAnfrageId}
                onChange={(e) => setSelectedAnfrageId(e.target.value)}
                className="flex-1 min-w-0 px-2 sm:px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary truncate"
              >
                <option value="">Ohne Zuordnung</option>
                {anfragen.map(anfrage => (
                  <option key={anfrage.id} value={anfrage.id}>
                    {anfrage.betreff.length > 30 ? anfrage.betreff.substring(0, 30) + '...' : anfrage.betreff}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div
            className={`border-2 border-dashed rounded-card p-8 text-center transition ${
              dragActive
                ? 'border-primary bg-primary-light'
                : 'border-border hover:border-primary'
            }`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            <input
              type="file"
              id="foto-upload"
              className="hidden"
              accept="image/*,.pdf"
              onChange={handleChange}
              disabled={uploading}
            />
            <label
              htmlFor="foto-upload"
              className="cursor-pointer block"
            >
              <Upload className={`w-12 h-12 mx-auto mb-4 ${dragActive ? 'text-primary' : 'text-text-muted'}`} />
              <p className="text-text font-medium mb-2">
                {uploading ? 'Wird hochgeladen...' : 'Datei hier ablegen oder klicken zum Auswaehlen'}
              </p>
              <p className="text-sm text-text-muted">
                JPG, PNG, GIF oder PDF (max. 10MB)
              </p>
            </label>
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
          {error}
        </div>
      )}

      {/* Filter by Anfrage */}
      {fotos && fotos.length > 0 && anfragen && anfragen.length > 0 && (
        <div className="flex items-center gap-3 pt-2 border-t border-border">
          <span className="text-sm text-text-muted">Filter:</span>
          <select
            value={filterAnfrageId}
            onChange={(e) => setFilterAnfrageId(e.target.value)}
            className="px-3 py-1.5 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="all">Alle Fotos ({fotos.length})</option>
            <option value="untagged">Ohne Zuordnung ({fotos.filter(f => !f.anfrageId).length})</option>
            {anfragen.map(anfrage => {
              const count = fotos.filter(f => f.anfrageId === anfrage.id).length;
              return (
                <option key={anfrage.id} value={anfrage.id}>
                  {anfrage.betreff} ({count})
                </option>
              );
            })}
          </select>
        </div>
      )}

      {/* Photo Gallery */}
      {filteredFotos.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {filteredFotos.map((foto) => (
            <div
              key={foto.id}
              className="relative bg-surface border border-border rounded-card overflow-hidden group"
            >
              {/* Image Preview */}
              <div
                className={`aspect-square bg-gray-100 flex items-center justify-center ${
                  !foto.dateiname.toLowerCase().endsWith('.pdf') ? 'cursor-pointer' : ''
                }`}
                onClick={() => openLightbox(foto)}
              >
                {foto.dateiname.toLowerCase().endsWith('.pdf') ? (
                  <div className="text-center p-4">
                    <ImageIcon className="w-12 h-12 mx-auto text-text-muted mb-2" />
                    <p className="text-xs text-text-muted truncate">
                      {foto.dateiname}
                    </p>
                  </div>
                ) : (
                  <img
                    src={getFotoUrl(foto)}
                    alt={foto.dateiname}
                    className="w-full h-full object-cover"
                  />
                )}
              </div>

              {/* Anfrage Tag Badge */}
              {foto.anfrage && (
                <div className="absolute top-2 left-2">
                  <span className="text-xs px-2 py-1 bg-primary text-white rounded-full shadow">
                    {foto.anfrage.betreff.length > 15
                      ? foto.anfrage.betreff.substring(0, 15) + '...'
                      : foto.anfrage.betreff}
                  </span>
                </div>
              )}

              {/* Action Buttons (shown on hover) - Klick auf Hintergrund öffnet Lightbox */}
              <div
                className="absolute inset-0 bg-black bg-opacity-50 opacity-0 group-hover:opacity-100 transition flex items-center justify-center gap-2 cursor-pointer"
                onClick={() => openLightbox(foto)}
              >
                <button
                  onClick={(e) => { e.stopPropagation(); handleDownload(foto); }}
                  className="p-2 bg-white rounded-lg hover:bg-gray-100 transition"
                  title="Download"
                >
                  <Download className="w-5 h-5 text-text" />
                </button>
                {onSkizzieren && !foto.dateiname.toLowerCase().endsWith('.pdf') && (
                  <button
                    onClick={(e) => { e.stopPropagation(); onSkizzieren(foto); }}
                    className="p-2 bg-white rounded-lg hover:bg-gray-100 transition"
                    title="Skizzieren"
                  >
                    <PenTool className="w-5 h-5 text-primary" />
                  </button>
                )}
                {!readOnly && anfragen && anfragen.length > 0 && (
                  <button
                    onClick={(e) => { e.stopPropagation(); setEditingFotoId(editingFotoId === foto.id ? null : foto.id); }}
                    className="p-2 bg-white rounded-lg hover:bg-gray-100 transition"
                    title="Anfrage zuordnen"
                  >
                    <Tag className="w-5 h-5 text-primary" />
                  </button>
                )}
                {!readOnly && (
                  <button
                    onClick={(e) => { e.stopPropagation(); handleDelete(foto.id); }}
                    className="p-2 bg-danger rounded-button hover:bg-red-700 transition"
                    title="Loeschen"
                  >
                    <Trash2 className="w-5 h-5 text-white" />
                  </button>
                )}
              </div>

              {/* Anfrage Selection Dropdown */}
              {editingFotoId === foto.id && (
                <div className="absolute inset-x-0 bottom-0 bg-white border-t border-border p-2 shadow-lg">
                  <select
                    value={foto.anfrageId || ''}
                    onChange={(e) => handleAnfrageChange(foto.id, e.target.value)}
                    className="w-full px-2 py-1.5 border border-border rounded text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                    autoFocus
                  >
                    <option value="">Keine Zuordnung</option>
                    {anfragen.map(anfrage => (
                      <option key={anfrage.id} value={anfrage.id}>
                        {anfrage.betreff}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Filename */}
              {editingFotoId !== foto.id && (
                <div className="p-2 bg-white border-t border-border">
                  <p className="text-xs text-text-muted truncate" title={foto.dateiname}>
                    {foto.dateiname}
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Empty State */}
      {(!fotos || fotos.length === 0) && !uploading && (
        <div className="text-center py-8 text-text-muted">
          <ImageIcon className="w-12 h-12 mx-auto mb-2 opacity-50" />
          <p>Noch keine Fotos vorhanden</p>
        </div>
      )}

      {/* Filtered Empty State */}
      {fotos && fotos.length > 0 && filteredFotos.length === 0 && (
        <div className="text-center py-8 text-text-muted">
          <ImageIcon className="w-12 h-12 mx-auto mb-2 opacity-50" />
          <p>Keine Fotos mit diesem Filter gefunden</p>
        </div>
      )}

      {/* Lightbox Modal - Mobile optimiert */}
      {lightboxFoto && (
        <div
          className="fixed inset-0 z-50 bg-black bg-opacity-95 flex items-center justify-center"
          onClick={() => setLightboxFoto(null)}
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
        >
          {/* Close Button - größer für Mobile, Safe Area beachten */}
          <button
            onClick={() => setLightboxFoto(null)}
            className="absolute top-4 right-4 p-3 text-white bg-black/30 hover:bg-white/20 active:bg-white/30 rounded-full transition z-20 safe-area-inset-top"
            style={{ marginTop: 'env(safe-area-inset-top, 0px)' }}
          >
            <X className="w-7 h-7 md:w-8 md:h-8" />
          </button>

          {/* Navigation - Previous (versteckt auf Mobile, Swipe nutzen) */}
          {filteredFotos.length > 1 && (
            <button
              onClick={(e) => { e.stopPropagation(); navigateLightbox(-1); }}
              className="hidden md:flex absolute left-4 p-4 text-white hover:bg-white/20 rounded-full transition z-10"
            >
              <ChevronLeft className="w-10 h-10" />
            </button>
          )}

          {/* Image Container mit Touch-Handling */}
          <div
            className="w-full h-full flex items-center justify-center p-4 md:p-8"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={getFotoUrl(lightboxFoto)}
              alt={lightboxFoto.dateiname}
              className="max-w-full max-h-full object-contain select-none"
              draggable={false}
            />
          </div>

          {/* Navigation - Next (versteckt auf Mobile, Swipe nutzen) */}
          {filteredFotos.length > 1 && (
            <button
              onClick={(e) => { e.stopPropagation(); navigateLightbox(1); }}
              className="hidden md:flex absolute right-4 p-4 text-white hover:bg-white/20 rounded-full transition z-10"
            >
              <ChevronRight className="w-10 h-10" />
            </button>
          )}

          {/* Mobile Navigation Buttons unten */}
          {filteredFotos.length > 1 && (
            <div className="md:hidden absolute bottom-20 left-0 right-0 flex justify-center gap-8 z-10">
              <button
                onClick={(e) => { e.stopPropagation(); navigateLightbox(-1); }}
                className="p-4 text-white bg-black/40 active:bg-white/20 rounded-full transition"
              >
                <ChevronLeft className="w-8 h-8" />
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); navigateLightbox(1); }}
                className="p-4 text-white bg-black/40 active:bg-white/20 rounded-full transition"
              >
                <ChevronRight className="w-8 h-8" />
              </button>
            </div>
          )}

          {/* Image Info - Mobile angepasst */}
          <div
            className="absolute bottom-4 left-4 right-4 text-white text-center"
            style={{ marginBottom: 'env(safe-area-inset-bottom, 0px)' }}
          >
            <p className="text-xs md:text-sm opacity-80 truncate px-4">{lightboxFoto.dateiname}</p>
            {filteredFotos.length > 1 && (
              <p className="text-xs opacity-60 mt-1">
                {currentLightboxIndex + 1} / {filteredFotos.length}
                <span className="hidden md:inline"> • Pfeiltasten zum Navigieren, ESC zum Schließen</span>
                <span className="md:hidden"> • Wischen zum Navigieren</span>
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default FotoUpload;
